// Mirrors REJECTION_FLAGS derived in @repo/shared/domain/File/flags.ts
// (the flags where rejectsDocument === true). Kept local rather than imported:
// form-components is rollup/babel-bundled without the shared TS package, matching
// the same copy-don't-import convention used in upload.js. Keep in sync with shared.
// (documentProblems.sync.test.jsx fails CI if these drift from flags.ts.)
export const REJECTION_FLAGS = new Set([
	'EXPIRED',
	'TYPE_MISMATCH',
	'NAME_MISMATCH',
	'DOB_MISMATCH',
	'FORGERY_SUSPECTED',
	'MISSING_REQUIRED_FIELDS',
	'INCOMPLETE_DOCUMENT',
	'MISSING_FRONT_SIDE',
	'MISSING_BACK_SIDE',
]);

// Mirrors POSITIVE_FLAGS in @repo/shared/domain/File/flags.ts (sentiment === 'positive').
// Positive flags are "passed extra checks", never a reason to block. Keep in sync with shared.
export const POSITIVE_FLAGS = new Set(['CDR_VERIFIED', 'LIMOSA_VERIFIED']);

// Mirrors the info-sentiment flags in @repo/shared/domain/File/flags.ts (DOCUMENT_FLAGS entries
// where sentiment === 'info'). Informational reasons a doc is in review (e.g. DEFAULT_IN_REVIEW) —
// surfaced to reviewers, never block the worker, and intentionally omitted from
// documentFlagMessages.js so the panel never shows them. Keep in sync with shared.
export const INFO_FLAGS = new Set(['DEFAULT_IN_REVIEW']);

// Build a unified, deduped problem list from a parsed file.
// Merges agentic `flags` and parser `reviewReason` codes into one shape.
// Returns: [{ code, severity: 'error'|'warning', label, description }]
export function buildProblems(file, messages) {
	if (!file || !messages) return [];
	const seen = new Set();
	const problems = [];

	// 1. Agentic flags — severity from the (locally mirrored) rejection list
	for (const code of file.flags ?? []) {
		if (seen.has(code)) continue;
		seen.add(code);
		const msg = messages.flags?.[code];
		if (!msg) continue; // omitted (e.g. positive CDR_VERIFIED) → not a "problem"
		problems.push({
			code,
			severity: REJECTION_FLAGS.has(code) ? 'error' : 'warning',
			label: msg.label,
			description: msg.description,
		});
	}

	// 2. reviewReason codes (BlinkID etc.) not already shown as a flag
	for (const code of file.reviewReason ?? []) {
		if (seen.has(code)) continue;
		seen.add(code);
		const msg = messages.reviewReasons?.[code] ?? messages.fallback;
		problems.push({ code, severity: 'warning', label: msg.label, description: msg.description });
	}

	return problems;
}

// A doc has something for the worker to acknowledge only when it carries a problem flag
// (any non-positive flag) or a reviewReason — i.e. exactly when `buildProblems` would render
// the acknowledge panel. A doc routed to review with neither (e.g. a clean doc whose type has
// `defaultInReview: true`, approved by the agentic API with no flags) has nothing to acknowledge,
// so it must NOT block "Next" — otherwise it's stuck (blocked, but no acknowledge button shown).
function hasReviewProblems(file) {
	const hasProblemFlag = (file?.flags ?? []).some((f) => !POSITIVE_FLAGS.has(f) && !INFO_FLAGS.has(f));
	const hasReviewReason = (file?.reviewReason?.length ?? 0) > 0;
	return hasProblemFlag || hasReviewReason;
}

// True when any uploaded doc is in review WITH problems the worker hasn't acknowledged yet.
// Used by sections to keep "Next" disabled until the in-review doc is acknowledged.
export function hasUnacknowledgedReview(submittedDocs) {
	// `reviewAcknowledged` is set true when the worker clicks "Continue anyway"; it both un-gates
	// "Next" this session and is persisted to the file on submit (same boolean on reload).
	return (
		Array.isArray(submittedDocs) &&
		submittedDocs.some((d) => d?.status === 'inReview' && !d.reviewAcknowledged && hasReviewProblems(d))
	);
}
